import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  MessageBar,
  MessageBarType,
  Spinner,
  SpinnerSize,
  Dropdown,
  IDropdownOption,
} from "@fluentui/react";
import { Grid } from "fluentui-react-grid";
import CancelButton from "@/ui-controls/CancelButton";
import SaveButton from "@/ui-controls/SaveButton";
import TextInput from "@/ui-controls/TextInput";
import { InboundWebhookSecurityTypeEnum, InboundWebhookProviderTypeEnum } from "@/types/webhooks";
import { createInboundWebhookProvider, updateInboundWebhookProvider, InboundWebhookProviderResponse } from "@/api/webhookProviders";

interface InboundWebhookProviderModel {
  InboundWebhookSecurityTypeId: number;
  InboundWebhookProviderTypeId: number;
  Description: string;
  ProviderUrl: string;
}

interface Props {
  mode: 'create' | 'edit';
  existingProvider?: InboundWebhookProviderResponse;
  onClose?: () => void;
  onSave?: (provider: any) => void;
  onShowSuccess?: (message: string) => void;
  onShowWarnings?: (messages: string[]) => void;
}

const securityTypeOptions: IDropdownOption[] = [
  {
    key: InboundWebhookSecurityTypeEnum.ApiKey,
    text: "API Key",
  },
  {
    key: InboundWebhookSecurityTypeEnum.OAuth2,
    text: "OAuth2",
  },
  {
    key: InboundWebhookSecurityTypeEnum.Svix,
    text: "Svix",
  },
];

const providerTypeOptions: IDropdownOption[] = [
  {
    key: InboundWebhookProviderTypeEnum.OCR,
    text: "OCR",
  },
  {
    key: InboundWebhookProviderTypeEnum.Manual,
    text: "Manual",
  },
];

const WebhookProviderForm = (props: Props) => {
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const isEditMode = props.mode === 'edit';
  const actionText = isEditMode ? 'updating' : 'creating';
  const buttonText = isEditMode ? 'Update' : 'Save';

  // Auto-clear error message after 8 seconds
  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => {
        setErrorMessage(null);
      }, 8000);
      return () => clearTimeout(timer);
    }
  }, [errorMessage]);

  const saveWebhookProvider = async (model: InboundWebhookProviderModel) => {
    setErrorMessage(null);
    setIsSaving(true);

    try {
      let result: InboundWebhookProviderResponse | boolean;
      let successMsg: string;

      if (isEditMode && props.existingProvider) {
        // Update existing provider
        const updateSuccess = await updateInboundWebhookProvider(props.existingProvider.Id, model);
        if (!updateSuccess) {
          throw new Error('Failed to update provider');
        }
        successMsg = `Webhook provider "${model.Description}" has been successfully updated.`;
        
        // For update, we need to create the full provider object to pass back
        result = {
          ...props.existingProvider,
          ...model
        };
      } else {
        // Create new provider
        result = await createInboundWebhookProvider(model);
        successMsg = `Webhook provider "${model.Description}" has been successfully created.`;
      }

      // Pass success message to parent to show at screen level
      props.onShowSuccess?.(successMsg);

      // Check for warning messages in the result and pass to parent (only for create)
      if (!isEditMode && result && typeof result === 'object' && 'Messages' in result) {
        const resultWithMessages = result as any;
        if (
          resultWithMessages?.Messages &&
          Array.isArray(resultWithMessages.Messages) &&
          resultWithMessages.Messages.length > 0
        ) {
          props.onShowWarnings?.(resultWithMessages.Messages);
        }
      }

      props.onSave?.(result);

      // Close modal after a brief delay to show success message
      setTimeout(() => {
        props.onClose?.();
      }, 2000);
    } catch (error: any) {
      console.error(`Error ${actionText} webhook provider:`, error);
      handleError(error);
    } finally {
      setIsSaving(false);
    }
  };

  const clearMessages = () => {
    setErrorMessage(null);
  };

  // Initialize form with existing values if in edit mode
  const getInitialValues = () => {
    if (isEditMode && props.existingProvider) {
      return {
        securityTypeId: props.existingProvider.InboundWebhookSecurityTypeId,
        providerTypeId: props.existingProvider.InboundWebhookProviderTypeId,
        description: props.existingProvider.Description,
        providerUrl: props.existingProvider.ProviderUrl || "",
      };
    }
    return {
      securityTypeId: InboundWebhookSecurityTypeEnum.ApiKey,
      providerTypeId: InboundWebhookProviderTypeEnum.OCR,
      description: "",
      providerUrl: "",
    };
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: getInitialValues(),
    validationSchema: Yup.object({
      securityTypeId: Yup.number().required("Security type is required"),
      providerTypeId: Yup.number().required("Provider type is required"),
      description: Yup.string()
        .required("Description is required")
        .max(100, "Description must be 100 characters or less"),
      providerUrl: Yup.string()
        .max(255, "Provider URL must be 255 characters or less")
        .url("Must be a valid URL")
        .nullable(),
    }),
    onSubmit: (values) => {
      if (isSaving) return; // Prevent multiple submissions

      const model: InboundWebhookProviderModel = {
        InboundWebhookSecurityTypeId: values.securityTypeId,
        InboundWebhookProviderTypeId: values.providerTypeId,
        Description: values.description,
        ProviderUrl: values.providerUrl || "",
      };
      saveWebhookProvider(model);
    },
  });

  const handleError = (error: any) => {
    if (error?.errors) {
      const fieldErrors = Object.values(error.errors) as string[][];
      if (fieldErrors.length && fieldErrors[0].length) {
        setErrorMessage(fieldErrors[0][0]);
        return;
      }
    }
    setErrorMessage(`An unexpected error occurred while ${actionText} the webhook provider. Please try again.`);
    console.error(error);
  };

  return (
    <>
      {/* full-screen overlay when saving */}
      {isSaving && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999, // Very high z-index to cover everything
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              backgroundColor: "white",
              padding: "24px",
              borderRadius: "8px",
              boxShadow: "0 4px 16px rgba(0, 0, 0, 0.3)",
            }}
          >
            <Spinner size={SpinnerSize.large} label={`${isEditMode ? 'Updating' : 'Saving'} webhook provider...`} />
          </div>
        </div>
      )}
      <form onSubmit={formik.handleSubmit} style={{ paddingTop: 25 }}>
        <Grid dir="ltr">
          {/* Security Type */}
          <Grid.Row>
            <Grid.Col sizeSm={12}>
              <Dropdown
                label="Security Type"
                options={securityTypeOptions}
                selectedKey={formik.values.securityTypeId}
                onChange={(_, option) => {
                  if (option) {
                    formik.setFieldValue("securityTypeId", Number(option.key));
                    clearMessages();
                  }
                }}
                disabled={isSaving}
                errorMessage={
                  formik.touched.securityTypeId && formik.errors.securityTypeId
                    ? formik.errors.securityTypeId
                    : undefined
                }
              />
            </Grid.Col>
          </Grid.Row>

          {/* Provider Type */}
          <Grid.Row>
            <Grid.Col sizeSm={12}>
              <Dropdown
                label="Provider Type"
                options={providerTypeOptions}
                selectedKey={formik.values.providerTypeId}
                onChange={(_, option) => {
                  if (option) {
                    formik.setFieldValue("providerTypeId", Number(option.key));
                    clearMessages();
                  }
                }}
                disabled={isSaving}
                errorMessage={
                  formik.touched.providerTypeId && formik.errors.providerTypeId
                    ? formik.errors.providerTypeId
                    : undefined
                }
              />
            </Grid.Col>
          </Grid.Row>

          {/* Description */}
          <Grid.Row>
            <Grid.Col sizeSm={12}>
              <TextInput
                label="Description"
                baseTypeProps={{
                  disabled: isSaving,
                  ...formik.getFieldProps("description"),
                  onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                    formik.handleChange(e);
                    clearMessages();
                  },
                }}
                hasError={
                  !!(formik.touched.description && formik.errors.description)
                }
                errorMessage={formik.errors.description}
              />
            </Grid.Col>
          </Grid.Row>

          {/* Provider URL */}
          <Grid.Row>
            <Grid.Col sizeSm={12}>
              <TextInput
                label="Provider URL (Optional)"
                baseTypeProps={{
                  disabled: isSaving,
                  ...formik.getFieldProps("providerUrl"),
                  onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                    formik.handleChange(e);
                    clearMessages();
                  },
                }}
                hasError={
                  !!(formik.touched.providerUrl && formik.errors.providerUrl)
                }
                errorMessage={formik.errors.providerUrl}
              />
            </Grid.Col>
          </Grid.Row>

          {/* buttons */}
          <Grid.Row className="panel-buttons">
            <Grid.Col>
              <SaveButton
                text={buttonText}
                baseTypeProps={{
                  type: "submit",
                  disabled: isSaving,
                }}
              />
            </Grid.Col>
            <Grid.Col>
              <CancelButton
                text="Cancel"
                onClick={() => {
                  clearMessages();
                  props.onClose?.();
                }}
                baseTypeProps={{ disabled: isSaving }}
              />
            </Grid.Col>
          </Grid.Row>
        </Grid>
      </form>
      {/* error bar */}
      {errorMessage && (
        <MessageBar
          messageBarType={MessageBarType.error}
          isMultiline={false}
          onDismiss={() => setErrorMessage(null)}
          styles={{
            root: {
              position: "fixed",
              bottom: 0,
              left: 0,
              width: "100%",
              zIndex: 10000, // Above overlay
            },
          }}
        >
          {errorMessage}
        </MessageBar>
      )}
    </>
  );
};

export default WebhookProviderForm;
